import { toast } from "react-toastify";
import ddAxios, { getEmpCode, getLatestServerDate, refreshServerDate } from "../../config/ddAxios";

export { getEmpCode, getLatestServerDate, refreshServerDate };

export const DETAIL_DESIGN_SOURCE_ID = "c4e54604-9a83-4065-b798-ad0e58673789";

export const PROJECT_TYPE_FILTER_OPTIONS = [
  { value: "all", label: "All" },
  { value: "detail_design", label: "Detail Design" },
  { value: "dpr", label: "DPR" },
  { value: "prebid", label: "Prebid" },
];

export const PROJECT_TYPE_SOURCE_IDS = {
  detail_design: DETAIL_DESIGN_SOURCE_ID,
  dpr: "dpr",
  prebid: "prebid",
};

const PROJECT_TYPE_ALIASES = {
  detaildesign: "detail_design",
  "detail-design": "detail_design",
  detail_design: "detail_design",
  dd: "detail_design",
  dpr: "dpr",
  prebid: "prebid",
  "pre-bid": "prebid",
  [DETAIL_DESIGN_SOURCE_ID]: "detail_design",
};

export const getProjectSourceId = (project) => {
  if (!project) return "";
  return String(
    project.source_id ||
    project.sourceId ||
    project.source?.id ||
    project.source?.source_id ||
    project.project_source_id ||
    ""
  ).trim();
};

export const getProjectTypeFilterValue = (project) => {
  const sourceId = String(getProjectSourceId(project) || "");
  if (sourceId === DETAIL_DESIGN_SOURCE_ID) return "detail_design";
  const raw = sourceId.toLowerCase().replace(/\s+/g, "_");
  return PROJECT_TYPE_ALIASES[raw] || (raw && PROJECT_TYPE_SOURCE_IDS[raw] ? raw : "all");
};

export const filterProjectsBySourceId = (projects = [], sourceId = DETAIL_DESIGN_SOURCE_ID) => {
  const list = Array.isArray(projects) ? projects : [];
  if (!sourceId) return list;
  const hasMeta = list.some((p) => getProjectSourceId(p));
  if (!hasMeta) return list;
  return list.filter((p) => {
    const sid = getProjectSourceId(p);
    return !sid || String(sid) === String(sourceId);
  });
};

export const resolveProjectsForType = ({
  projects = [],
  typeKey = "all",
  includeProjectId,
  hasSourceMeta,
  remoteList,
}) => {
  const sourceId = PROJECT_TYPE_SOURCE_IDS[typeKey];
  const base = Array.isArray(remoteList) && remoteList.length ? remoteList : projects;
  if (!sourceId || typeKey === "all" || !hasSourceMeta) {
    if (!sourceId || typeKey === "all") return projects;
    return base;
  }
  const filtered = base.filter((p) => String(getProjectSourceId(p)) === String(sourceId));
  if (includeProjectId && !filtered.some((p) => String(p.id || p.project_id) === String(includeProjectId))) {
    const keep = projects.find((p) => String(p.id || p.project_id) === String(includeProjectId));
    if (keep) return [keep, ...filtered];
  }
  return filtered;
};

const unwrapList = (data) => {
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.results)) return data.results;
  if (Array.isArray(data?.data?.results)) return data.data.results;
  if (Array.isArray(data?.data?.projects)) return data.data.projects;
  if (Array.isArray(data?.data)) return data.data;
  if (Array.isArray(data?.projects)) return data.projects;
  if (Array.isArray(data?.planners)) return data.planners;
  if (Array.isArray(data?.employees)) return data.employees;
  if (Array.isArray(data?.time_logs)) return data.time_logs;
  if (Array.isArray(data?.timelogs)) return data.timelogs;
  if (Array.isArray(data?.worklogs)) return data.worklogs;
  if (Array.isArray(data?.logs)) return data.logs;
  return [];
};

const extractErrorMessage = (error, fallback) => {
  const backend = error?.response?.data;
  return (
    backend?.errors?.non_field_errors?.[0] ||
    backend?.message ||
    (typeof backend?.detail === "string" ? backend.detail : "") ||
    (error?.response?.status === 400 ? "Duplicate sub-activity is not allowed." : "") ||
    error?.message ||
    fallback
  );
};

const tryGet = async (paths) => {
  let lastError = null;
  for (const path of paths) {
    try {
      const res = await ddAxios.get(path);
      return res.data;
    } catch (err) {
      lastError = err;
      if (err?.response?.status && err.response.status < 500 && err.response.status !== 404) {
        throw err;
      }
    }
  }
  throw lastError || new Error("Request failed");
};

const tryGetAny = async (paths) => {
  let lastError = null;
  for (const path of paths) {
    try {
      const res = await ddAxios.get(path);
      return res.data;
    } catch (err) {
      lastError = err;
    }
  }
  throw lastError || new Error("Request failed");
};

let projectsListCache = { sourceId: "", list: null, promise: null };

export const fetchProjectsListSimple = async (sourceId = DETAIL_DESIGN_SOURCE_ID) => {
  if (projectsListCache.list && projectsListCache.sourceId === sourceId) {
    return projectsListCache.list;
  }
  if (projectsListCache.promise && projectsListCache.sourceId === sourceId) {
    return projectsListCache.promise;
  }

  projectsListCache.sourceId = sourceId;
  projectsListCache.promise = (async () => {
    try {
      const data = await tryGetAny(["detaildesign/get-projects-lists/"]);
      const list = filterProjectsBySourceId(unwrapList(data), sourceId);
      projectsListCache.list = list;
      return list;
    } catch {
      const data = await tryGetAny([
        "/projects/",
        "/projects/simple/",
        "/bd-detaildesign/projects/?page_size=1000",
      ]);
      const list = unwrapList(data).filter((p) => String(getProjectSourceId(p)) === String(sourceId));
      projectsListCache.list = list;
      return list;
    }
  })().finally(() => {
    projectsListCache.promise = null;
  });

  return projectsListCache.promise;
};

export const fetchProjectDetails = async (projectId) => {
  if (!projectId) return null;
  const data = await tryGet([
    `detaildesign/get-projectdetails-byid/${projectId}/`,
    `/bd-detaildesign/projects/${projectId}/`,
  ]);
  return data;
};

export const getProjectsByTypeKey = async (typeKey) => {
  const sourceId = typeKey === "detail_design" || typeKey === "all"
    ? DETAIL_DESIGN_SOURCE_ID
    : PROJECT_TYPE_SOURCE_IDS[typeKey];
  if (!sourceId) return [];
  try {
    const data = await tryGetAny([
      `/projects/?page_size=1000&source_id=${encodeURIComponent(sourceId)}`,
      `/bd-detaildesign/projects/?page_size=1000&source_id=${encodeURIComponent(sourceId)}`,
    ]);
    return filterProjectsBySourceId(unwrapList(data), sourceId);
  } catch {
    return [];
  }
};

export const fetchTaskPlanners = async ({ activeTab, date } = {}) => {
  const empCode = getEmpCode();
  const selectedDate = date || new Date().toISOString().split("T")[0];
  const isComparison = activeTab === "User Tasks" || activeTab === "TL Tasks";

  if (isComparison) {
    const data = await tryGet([
      `detaildesign/time-planer/comparison/?date=${selectedDate}`,
      `detaildesign/time-planer/team/?date=${selectedDate}`,
    ]);
    return {
      planners: unwrapList(data?.planners || data),
      data: {
        employees: data?.employees || unwrapList(data),
        summary: data?.summary || null,
      },
    };
  }

  const userQuery = empCode ? `&user=${encodeURIComponent(empCode)}` : "";
  const data = await tryGet([
    `detaildesign/time-planer/?date=${selectedDate}${userQuery}`,
    `detaildesign/time-planer/?date=${selectedDate}`,
  ]);
  return {
    planners: unwrapList(data),
    data: data?.employees ? data : null,
  };
};

const toDateTime = (date, time) => `${date}T${time}:00`;

const buildDuration = (date, startTime, endTime, status) => {
  if (status !== "WORKED") {
    return {
      startDateTime: `${date}T00:00:00`,
      endDateTime: `${date}T23:59:59`,
      durationSeconds: 86400,
    };
  }
  if (!startTime || !endTime) throw new Error("Please enter both start and end time");
  const startDateTime = toDateTime(date, startTime);
  const endDateTime = toDateTime(date, endTime);
  const start = new Date(startDateTime);
  const end = new Date(endDateTime);
  if (start >= end) throw new Error("End time must be after start time");
  return {
    startDateTime,
    endDateTime,
    durationSeconds: Math.round((end - start) / 1000),
  };
};

export const saveDailyWorkplan = async (logs = []) => {
  const userUUID = getEmpCode();
  if (!userUUID) throw new Error("User not authenticated");

  const payload = logs.map((log) => {
    const status = log.status || "WORKED";
    const { startDateTime, endDateTime, durationSeconds } = buildDuration(
      log.date,
      log.startTime,
      log.endTime,
      status
    );
    return {
      project: log.projectId,
      user: userUUID,
      subactivity: log.subActivityId,
      entry_type: status === "WORKED" ? "WORK_LOG" : "LEAVE",
      status: "not_done",
      start_time: startDateTime,
      end_time: endDateTime,
      duration: durationSeconds,
      work_type: log.workType,
      date: log.date,
      note: log.note || "",
    };
  });

  try {
    const response = await ddAxios.post("detaildesign/time-planer/", payload);
    toast.success(`Successfully saved ${payload.length} plan(s)`);
    return response.data;
  } catch (error) {
    const message = extractErrorMessage(error, "Failed to save plan");
    toast.error(message);
    throw error;
  }
};

export const updateDailyWorkplan = async (logs = []) => {
  const userUUID = getEmpCode();
  if (!userUUID) throw new Error("User not authenticated");

  const payload = logs.map((log) => {
    const status = log.status || "WORKED";
    const { startDateTime, endDateTime, durationSeconds } = buildDuration(
      log.date,
      log.start_time,
      log.end_time,
      status
    );
    return {
      id: log.id,
      project: log.project,
      user: userUUID,
      subactivity: log.subactivity,
      entry_type: status === "WORKED" ? "WORK_LOG" : "LEAVE",
      status: status === "WORKED" ? "COMPLETED" : "ABSENT",
      start_time: startDateTime,
      end_time: endDateTime,
      duration: durationSeconds,
      work_type: log.work_type,
      date: log.date,
      note: log.note || "",
    };
  });

  try {
    const response = await ddAxios.put("detaildesign/time-planer/bulk-update/", { planners: payload });
    toast.success("Plan updated successfully");
    return response.data;
  } catch (error) {
    const message = extractErrorMessage(error, "Failed to update plan");
    toast.error(message);
    throw error;
  }
};

export const saveDailyWorkLog = async (logOrLogs, options = {}) => {
  if (Array.isArray(logOrLogs)) {
    const results = [];
    for (const log of logOrLogs) {
      results.push(await saveDailyWorkLog(log, { silent: true }));
    }
    if (!options.silent) {
      toast.success(`Work log${results.length > 1 ? "s" : ""} saved successfully`);
    }
    return results;
  }

  const {
    projectId,
    subActivityId,
    date,
    startTime,
    endTime,
    start_time,
    end_time,
    work_type,
    workType,
    note,
    status = "WORKED",
    stage,
    project,
    subactivity,
  } = logOrLogs || {};
  const userUUID = getEmpCode();
  if (!userUUID) {
    toast.error("User not authenticated");
    throw new Error("User not authenticated");
  }

  try {
    const { startDateTime, endDateTime, durationSeconds } = buildDuration(
      date,
      startTime || start_time,
      endTime || end_time,
      status
    );
    const response = await ddAxios.post("detaildesign/bd-timelog/", {
      project: projectId || project,
      user: userUUID,
      // subactivity: subActivityId || subactivity,
      // stage,
      entry_type: status === "WORKED" ? "WORK_LOG" : "LEAVE",
      status: status === "WORKED" ? "COMPLETED" : "ABSENT",
      start_time: startDateTime,
      end_time: endDateTime,
      // duration: durationSeconds,
      // work_type: work_type || workType,
      note: note || (status === "WORKED" ? "Worked on task" : "No work done"),
    });
    if (!options.silent) {
      toast.success(
        status === "WORKED"
          ? `Work logged! ${(durationSeconds / 3600).toFixed(2)} hours recorded`
          : "Leave record saved successfully"
      );
    }
    return response.data;
  } catch (error) {
    const message = extractErrorMessage(error, "Failed to save record");
    toast.error(message);
    throw error;
  }
};

export const saveDailyWorkLogBulk = async (logs = []) => saveDailyWorkLog(logs);

export const fetchProjectWorklogs = async ({ projectId, period = "overall", empCode } = {}) => {
  if (!projectId) throw new Error("project_id is required");
  const qs = new URLSearchParams();
  if (period && period !== "overall") qs.set("period", period);
  if (empCode) qs.set("emp_code", empCode);
  const query = qs.toString();
  const pid = encodeURIComponent(String(projectId));
  const paths = [`detaildesign/project-worklogs/${pid}/`];
  if (query) paths.unshift(`detaildesign/project-worklogs/${pid}/?${query}`);
  const data = await tryGet(paths);
  const worklogs = Array.isArray(data?.worklogs) ? data.worklogs : unwrapList(data);
  return {
    project_id: data?.project_id || projectId,
    project_code: data?.project_code || "",
    project_name: data?.project_name || "",
    period: data?.period || period,
    total_worklogs: data?.total_worklogs ?? worklogs.length,
    total_hours: data?.total_hours ?? 0,
    worklogs,
  };
};

export const syncTenderToDetailDesign = async ({ tender_id, created_by }) => {
  if (!tender_id) throw new Error("tender_id is required");
  const payload = {
    tender_id,
    created_by: created_by || getEmpCode(),
  };
  const response = await ddAxios.post("/bd-detaildesign/sync-from-tender/", payload);
  return response.data;
};
